// ABOUTME: Zod path contract and types for the Skylegs → TechLog flight transformation.
import type { SkylegsUser } from '../../../../activities/api/skylegs/skylegs.zod';
import {
  ZSkylegsAircraftRef,
  ZSkylegsAirportRef,
  ZSkylegsFlight,
  ZSkylegsUser,
  ZSkylegsUserAgenda,
} from '../../../../activities/api/skylegs/skylegs.zod';
import { ZStandardizedFlight } from '../../../../types/standardizedFlight.zod';
import type { processEntityWorkflowInput } from '../../../../types/transmormer';
import { z } from 'zod';

/**
 * Input validation schema for the Skylegs → TechLog flight transformation.
 * Flat structure: skylegs.objectName.
 */
export const ZSkylegsToTechlogFlightInput = z.object({
  skylegs: z.object({
    // Two tightenings of the canonical flight schema, both specific to this path:
    // only READY flights are imported, and the embedded relations are nullable
    // canonically because a `view=` projection drops them. This path never
    // projects, and the aircraft and airport matches cannot be made without them.
    flight: ZSkylegsFlight.extend({
      status: z.literal('READY'),
      aircraft: ZSkylegsAircraftRef,
      departure: ZSkylegsAirportRef,
      arrival: ZSkylegsAirportRef,
    }),
    crew: z.array(ZSkylegsUserAgenda),
    organisationUsers: z.array(ZSkylegsUser),
  }),
});

/**
 * Output validation schema. The TechLog destination boundary for flights is
 * `StandardizedFlight`: the facade's flight handler feeds it to
 * `buildTechlogFlightsAndTripFromStandardizedFlights`, which resolves the
 * registration, airport ICAOs and crew emails into TechLog ids.
 */
export const ZSkylegsToTechlogFlightOutput = ZStandardizedFlight;

// Type exports - inferred from Zod schemas (single source of truth)
export type SkylegsToTechlogFlightInput = z.infer<
  typeof ZSkylegsToTechlogFlightInput
>;
export type SkylegsToTechlogFlightOutput = z.infer<
  typeof ZSkylegsToTechlogFlightOutput
>;

/**
 * Child input for one flight of a polling cycle. The organisation user list is
 * fetched once per cycle rather than once per flight, because it is identical
 * for every flight and each fetch spends a rate-limited Skylegs API credit, so
 * it travels to each child here. Declaring it makes that a compile-checked
 * contract between `skylegsGetFlights` and this path's context provider, which
 * `processEntityWorkflowInput` cannot give on its own — it carries an index
 * signature that accepts any extra key as `any`.
 */
export type SkylegsToTechlogFlightProcessEntityInput =
  processEntityWorkflowInput & {
    organisationUsers: SkylegsUser[];
  };
